/**
 * The trailer load diagram: what the API's seeded layout means on screen.
 *
 * Pure, like the rest of the mapping layer. The layout says where each pallet
 * position IS; the loaded count says which of them are already full. Combining
 * the two here — rather than in the component — is what keeps "which cell is
 * next" a tested rule instead of template arithmetic.
 */

export type TrailerLayoutCell = {
  /** 1-based, row 1 at the nose of the trailer. */
  row: number
  /** 1 (left), 2 (right), or 1.5 (centred between the two). */
  column: number
  /** The order pallets go on, 1-based and contiguous. */
  sequence: number
}

export type TrailerLayoutInfo = {
  palletCount: number
  rows: number
  /** Ordered by sequence. */
  cells: TrailerLayoutCell[]
}

export type TrailerLayoutDto = {
  palletCount?: number | null
  rows?: number | null
  cells?: Array<{
    row?: number | null
    column?: number | string | null
    sequence?: number | null
  }> | null
}

/**
 * The API's layout, checked and ordered.
 *
 * A cell without a usable position is dropped rather than drawn at 0×0 — a
 * diagram with a hole is odd, but one with a phantom cell floating outside the
 * trailer tells a driver to put a pallet somewhere that does not exist.
 * `column` is compared loosely because numeric(2,1) can arrive as a string
 * from JSON serializers that guard decimals.
 */
export function mapTrailerLayout(dto: TrailerLayoutDto): TrailerLayoutInfo {
  const cells = (dto.cells ?? [])
    .map(cell => ({
      row: cell.row ?? 0,
      column: Number(cell.column ?? 0),
      sequence: cell.sequence ?? 0,
    }))
    .filter(cell => cell.row >= 1 && cell.sequence >= 1 && [1, 1.5, 2].includes(cell.column))
    .sort((a, b) => a.sequence - b.sequence)

  return {
    palletCount: dto.palletCount ?? cells.length,
    rows: dto.rows ?? Math.max(0, ...cells.map(c => c.row)),
    cells,
  }
}

export type CellState = 'previous' | 'loaded' | 'pending' | 'empty' | 'other'

/**
 * What one cell should look like, given what is on the truck.
 *
 * The diagram is the whole TRAILER, so it spans every order on the trip.
 * Pallets go on strictly in sequence (ID28's load sequencing), which is what
 * lets three counts describe it completely and means no per-pallet position is
 * ever recorded:
 *
 * - `previous` — put on by an order earlier in the trip's sequence. Already
 *   freight on this truck, but not this order's work.
 * - `loaded` — this order's, so far.
 * - `pending` — on the forks and judged good, either way: the positions the
 *   held pallets will take when the door is scanned, or — on Unload Truck —
 *   the last aboard positions, whose pallets are on the forks and about to
 *   come off (user, 2026-09-03).
 * - `empty` — still to come, and this order's to fill.
 * - `other` — past this order's share of the trip: a LATER order's position.
 *   Still to come on Load Truck; on Unload Truck, freight aboard between the
 *   doors and this order's. Not this order's work either way, and drawn
 *   slashed like `previous` (user, 2026-09-14).
 *
 * @param loadedBefore pallets from earlier orders; the offset this order's
 *   own fill starts at.
 * @param pending valid pallets on the forks about to go ON, not yet sent.
 * @param leaving valid pallets on the forks about to come OFF — they still
 *   count in `loadedCount` until the location is scanned.
 * @param orderCapacity pallets THIS order asks for — its share of the
 *   trailer, starting after `loadedBefore`. 0 when unknown, and then nothing
 *   is marked `other`: the diagram never slashes a cell on a guess.
 */
export function cellState(
  cell: TrailerLayoutCell,
  loadedCount: number,
  loadedBefore = 0,
  pending = 0,
  leaving = 0,
  orderCapacity = 0,
): CellState {
  if (cell.sequence <= loadedBefore) return 'previous'
  const lastLoaded = loadedBefore + loadedCount
  if (cell.sequence <= lastLoaded) {
    return cell.sequence > lastLoaded - leaving ? 'pending' : 'loaded'
  }
  // Judged after what is aboard, so freight is never hidden behind a slash:
  // a position past this order's share is another order's only while it is
  // not this order's pallet sitting there.
  if (orderCapacity > 0 && cell.sequence > loadedBefore + orderCapacity) return 'other'
  return cell.sequence <= lastLoaded + pending ? 'pending' : 'empty'
}

/**
 * One order's run of positions on the trailer, for a truck that carries
 * several (owner, 2026-10-13): each position shows its order's place on the
 * truck, so the driver can see where one order ends and the next begins.
 */
export type OrderBlock = {
  /** The order's place on the truck, 1-based: the number drawn in its cells. */
  position: number
  /** How many positions it takes, in sequence after the blocks before it. */
  size: number
  /** The order pallets go on now: its empty positions are outlined. */
  current: boolean
}

/**
 * The blocks for a truck's orders, in loading sequence.
 *
 * An order behind the one shown takes the positions of what it actually put
 * aboard, because that is where the next order's pallets start (the same
 * count `loadedBefore` is made of); the one shown and those after it take
 * what they ask for. One order needs no marking, so it has no blocks.
 */
export function orderBlocks(
  orders: ReadonlyArray<{ required: number, aboard: number, current: boolean }>,
  shownIndex: number,
): OrderBlock[] {
  if (orders.length < 2) return []
  return orders.map((order, index) => ({
    position: index + 1,
    size: index < shownIndex ? order.aboard : Math.max(order.required, order.aboard),
    current: order.current,
  }))
}

/** The block a position falls in, or null past the last of them. */
export function blockOfCell(sequence: number, blocks: readonly OrderBlock[]): OrderBlock | null {
  let end = 0
  for (const block of blocks) {
    end += block.size
    if (sequence <= end) return block
  }
  return null
}
